import React from 'react'
import { Box, Typography } from '@mui/material'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts'
import { PieChartStoreData, QueryMode } from '../types'
import { CustomPieTooltip } from './ChartTooltips'
import { generateChartPalette, getScoreColor } from '../../../../../../../utils/methods/chartColors'
import { useStorePerformanceContext } from '../context/StorePerformanceContext'

interface PieChartViewProps {
  pieChartData: PieChartStoreData[]
  pieChartLoading: boolean
  queryMode: QueryMode
}

const RADIAN = Math.PI / 180
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}: {
  cx: number
  cy: number
  midAngle: number
  innerRadius: number
  outerRadius: number
  percent: number
}) => {
  if (percent < 0.05) return null
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5
  const x = cx + radius * Math.cos(-midAngle * RADIAN)
  const y = cy + radius * Math.sin(-midAngle * RADIAN)

  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight={600}
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  )
}

const PieChartView = ({ pieChartData, pieChartLoading, queryMode }: PieChartViewProps) => {
  const { colors, t } = useStorePerformanceContext()
  const PIE_COLORS = React.useMemo(() => generateChartPalette(colors), [colors])

  const labelColorMap = React.useMemo(() => {
    const map = new Map<string, string>()
    const labelSet = new Set<string>()
    pieChartData.forEach((store) => {
      store.data.forEach((slice) => labelSet.add(slice.label))
    })
    let index = 0
    labelSet.forEach((label) => {
      map.set(label, PIE_COLORS[index % PIE_COLORS.length])
      index++
    })
    return map
  }, [pieChartData, PIE_COLORS])

  const getChartTitle = (store: PieChartStoreData): string => {
    if (queryMode === 'many-units-one-questionnaire') {
      return store.storeName
    }
    return store.questionnaireName
  }

  if (pieChartLoading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '14px', color: colors.color9 }}>
          {t('general.loading')}
        </Typography>
      </Box>
    )
  }

  if (pieChartData.length === 0) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: '14px', color: colors.color9 }}>
          {t('general.ninformationNotFound')}
        </Typography>
      </Box>
    )
  }

  const allLabels = Array.from(labelColorMap.keys())

  if (pieChartData.length === 1) {
    const store = pieChartData[0]
    const totalValue = store.data.reduce((sum, item) => sum + item.value, 0)
    const enrichedData = store.data.map((slice) => ({
      ...slice,
      _metaScore: store.score,
    }))

    return (
      <Box
        sx={{
          display: 'flex',
          gap: '32px',
          alignItems: 'center',
          flexWrap: 'wrap',
          px: '24px',
          py: '16px',
        }}
      >
        <Box sx={{ width: '180px', height: '180px', flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={enrichedData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={80}
                innerRadius={0}
                fill="#8884d8"
                dataKey="value"
                nameKey="label"
              >
                {enrichedData.map((entry) => (
                  <Cell
                    key={`cell-${entry.label}`}
                    fill={labelColorMap.get(entry.label) || '#8884d8'}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </Box>

        <Box sx={{ flex: 1, minWidth: '280px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '12px 24px',
              width: '100%',
              '@media (max-width: 768px)': {
                gridTemplateColumns: 'repeat(2, 1fr)',
              },
            }}
          >
            {store.data.map((slice) => {
              const percent = totalValue > 0 ? Math.round((slice.value / totalValue) * 100) : 0
              const sliceColor = labelColorMap.get(slice.label) || '#8884d8'
              return (
                <Box key={slice.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: `1px solid ${sliceColor}`, pb: '6px' }}>
                  <Box
                    sx={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '2px',
                      backgroundColor: sliceColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: colors.color13,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {slice.label}
                  </Typography>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, color: colors.color13, flexShrink: 0 }}>
                    {percent}%
                  </Typography>
                </Box>
              )
            })}
          </Box>

          <Box
            sx={{
              mt: '10px',
              pt: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontSize: '13px', color: colors.color9 }}>
              {t('dashboard.storePerformance.chart.periodPerformance')}
            </Typography>
            <Box
              sx={{
                backgroundColor: getScoreColor(store.score, colors),
                color: '#fff',
                fontSize: '12px',
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: '4px',
                minWidth: '40px',
                textAlign: 'center',
                flexShrink: 0,
              }}
            >
              {store.score}%
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {allLabels.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            mb: '16px',
            flexWrap: 'wrap',
          }}
        >
          {allLabels.map((label) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Box
                sx={{
                  width: '12px',
                  height: '8px',
                  backgroundColor: labelColorMap.get(label),
                  flexShrink: 0,
                }}
              />
              <Typography sx={{ fontSize: '12px', color: colors.color13 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          px: '20px',
          pb: '20px',
          '@media (max-width: 1440px)': {
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '14px',
            px: '16px',
          },
          '@media (max-width: 1280px)': {
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px',
            px: '16px',
          },
          '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr',
            px: '12px',
          },
        }}
      >
        {pieChartData.map((store) => {
          const chartTitle = getChartTitle(store)
          const enrichedData = store.data.map((slice) => ({
            ...slice,
            _metaScore: store.score,
          }))
          return (
            <Box
              key={`${store.storeId}-${store.questionnaireId}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingBottom: '20px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: colors.color13,
                  textAlign: 'center',
                  minHeight: '44px',
                  maxHeight: '44px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                  px: '8px',
                }}
              >
                {chartTitle}
              </Typography>
              <Box sx={{ width: '100%', height: '180px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={enrichedData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      innerRadius={0}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="label"
                    >
                      {enrichedData.map((entry) => (
                        <Cell
                          key={`cell-${entry.label}`}
                          fill={labelColorMap.get(entry.label) || '#8884d8'}
                        />
                      ))}
                    </Pie>
                    {queryMode === 'many-units-one-questionnaire' && (
                      <Tooltip content={<CustomPieTooltip t={t} themeColors={colors} />} />
                    )}
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export { PieChartView }
